import { THEME_NAMES, type GameConfig, type ThemeName } from './types';

/** Used per field when the release has no valid value (and before the first bootstrap). */
export const defaultConfig: GameConfig = {
  maxLevel: 100,
  progressionOrder: Array.from({ length: 100 }, (_, i) => i + 1),
  lives: 3,
  startingCoins: 75,
  hintCost: 15,
  reviveCost: 25,
  reviveSeconds: 30,
  rewardByTier: [10, 12, 15, 18, 20],
  perfectBonus: 10,
  firstClearBonus: 20,
  dailyReward: 50,
  dailyMaxAttempts: 3,
  themePrices: { Default: 0, Gold: 100, Coral: 150, Teal: 200 },
  rewardedAds: true,
  dailyChallenge: true,
};

/**
 * Reads the game config from the release's client config: `economy` (coins and prices),
 * `mechanics` (lives, timers, progression, daily attempts) and `features` (switches).
 * A missing or invalid value keeps the fallback, field by field.
 */
export function configFromRelease(data: unknown, fallback: GameConfig = defaultConfig): GameConfig {
  const economy = section(data, 'economy');
  const mechanics = section(data, 'mechanics');
  const features = section(data, 'features');
  const maxLevel = int(mechanics.maxLevel, 1, 100) ?? fallback.maxLevel;
  return {
    maxLevel,
    progressionOrder: order(mechanics.progressionOrder, maxLevel) ?? fallback.progressionOrder.filter(n => n <= maxLevel),
    lives: int(mechanics.lives, 1, 10) ?? fallback.lives,
    startingCoins: int(economy.startingCoins, 0, 1_000_000) ?? fallback.startingCoins,
    hintCost: int(economy.hintCost, 0, 1_000_000) ?? fallback.hintCost,
    reviveCost: int(economy.reviveCost, 0, 1_000_000) ?? fallback.reviveCost,
    reviveSeconds: int(mechanics.reviveSeconds, 5, 600) ?? fallback.reviveSeconds,
    rewardByTier: tiers(economy.rewardByTier) ?? fallback.rewardByTier,
    perfectBonus: int(economy.perfectBonus, 0, 1_000_000) ?? fallback.perfectBonus,
    firstClearBonus: int(economy.firstClearBonus, 0, 1_000_000) ?? fallback.firstClearBonus,
    dailyReward: int(economy.dailyReward, 0, 1_000_000) ?? fallback.dailyReward,
    dailyMaxAttempts: int(mechanics.dailyMaxAttempts, 1, 100) ?? fallback.dailyMaxAttempts,
    themePrices: prices(economy.themePrices, fallback.themePrices),
    rewardedAds: bool(features.rewardedAds) ?? fallback.rewardedAds,
    dailyChallenge: bool(features.dailyChallenge) ?? fallback.dailyChallenge,
  };
}

function section(data: unknown, name: string): Record<string, unknown> {
  const value = isObject(data) ? data[name] : undefined;
  return isObject(value) ? value : {};
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function int(value: unknown, min: number, max: number): number | undefined {
  return Number.isSafeInteger(value) && (value as number) >= min && (value as number) <= max ? (value as number) : undefined;
}

function bool(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/** Five reward tiers (levels 1–20, 21–40, ...). */
function tiers(value: unknown): number[] | undefined {
  if (!Array.isArray(value) || value.length !== 5) return undefined;
  const out = value.map(v => int(v, 0, 1_000_000));
  return out.every(v => v !== undefined) ? (out as number[]) : undefined;
}

/** A play order over 1..maxLevel: every level exactly once. */
function order(value: unknown, maxLevel: number): number[] | undefined {
  if (!Array.isArray(value) || value.length !== maxLevel) return undefined;
  const seen = new Set<number>();
  for (const v of value) {
    const n = int(v, 1, maxLevel);
    if (n === undefined || seen.has(n)) return undefined;
    seen.add(n);
  }
  return value as number[];
}

function prices(value: unknown, fallback: Record<ThemeName, number>): Record<ThemeName, number> {
  const given = isObject(value) ? value : {};
  const out = { ...fallback };
  for (const name of THEME_NAMES) out[name] = int(given[name], 0, 1_000_000) ?? fallback[name];
  return out;
}
